import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, CreditCard, BookUser, Shield, UserCircle, LogOut, Menu, X, Users, Scan, Handshake, UserCheck, ShoppingBag, Package, Contact, Palette, QrCode, Sparkles, Zap, FileText, IndianRupee, Eye, Wrench, ChevronDown } from 'lucide-react';
import AuthButton from './AuthButton';

const Sidebar = ({ userCard, isMobileOpen, setIsMobileOpen }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Always use collapsed/minimized view
  const isCollapsed = true;

  // Check if user is admin
  const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];
  const isAdmin = (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) || user?.isAdmin === true;

  // Main navigation items - visible to all users
  const mainMenuItems = [
    { label: 'Home', path: '/', icon: Home, show: true },
    { label: 'Features', path: '/features', icon: Sparkles, show: !user },
    { label: 'How NFC', path: '/how-nfc-works', icon: Zap, show: !user },
    { label: 'Tools', path: '/tools', icon: Wrench, show: !user },
    { label: 'Shop', path: '/shop', icon: ShoppingBag, show: true },
    { label: 'Blog', path: '/blog', icon: FileText, show: !user },
    { label: 'Pricing', path: '/pricing', icon: IndianRupee, show: !user },
  ];

  // User-specific menu items - visible only when logged in
  const userMenuItems = [
    { label: 'Contacts', path: '/contacts', icon: Contact, show: user },
    { label: 'Leads', path: '/visitors', icon: Eye, show: user },
    { label: 'My Designs', path: '/my-designs', icon: Palette, show: user },
    { label: 'CRM', path: '/crm', icon: Handshake, show: user },
    { label: 'Team', path: '/team-settings', icon: Users, show: user },
    { label: 'Admin', path: '/admin', icon: Shield, show: isAdmin },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
    // Keep sidebar state when navigating
  };

  const handleSignOut = () => {
    signOut();
    setIsMobileOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[55]"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar - Always Minimized */}
      <aside className={`
        fixed top-[3.5rem] sm:top-[4rem] bottom-0 left-0 z-[55]
        bg-white border-r-2 border-gray-200 shadow-lg
        transition-all duration-300 ease-in-out
        w-20
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">

          {/* User Avatar with Profile Label - Links to Profile page */}
          {user && (
            <div className="p-3 border-b border-gray-200">
              <Link 
                to="/profile" 
                className={`group cursor-pointer flex flex-col items-center gap-1 ${
                  isActivePath('/profile') ? 'opacity-100' : 'opacity-90'
                }`}
                onClick={() => setIsMobileOpen(false)}
                title="Go to Profile"
              >
                {user?.photoURL ? (
                  <>
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'User'} 
                      className={`w-10 h-10 rounded-full object-cover border-2 transition-all group-hover:shadow-lg ${
                        isActivePath('/profile') ? 'border-amber-500 shadow-md' : 'border-amber-200 group-hover:border-amber-400'
                      }`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                    />
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 items-center justify-center group-hover:from-amber-500 group-hover:to-yellow-500 transition-all group-hover:shadow-lg" style={{ display: 'none' }}>
                      <span className="text-white text-sm font-bold">
                        {user.displayName?.charAt(0) || user.email?.charAt(0)}
                      </span>
                    </div>
                  </>
                ) : userCard?.profileImage ? (
                  <>
                    <img 
                      src={userCard.profileImage} 
                      alt={user.displayName || 'User'} 
                      className={`w-10 h-10 rounded-full object-cover border-2 transition-all group-hover:shadow-lg ${
                        isActivePath('/profile') ? 'border-amber-500 shadow-md' : 'border-amber-200 group-hover:border-amber-400'
                      }`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                    />
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 items-center justify-center group-hover:from-amber-500 group-hover:to-yellow-500 transition-all group-hover:shadow-lg" style={{ display: 'none' }}>
                      <span className="text-white text-sm font-bold">
                        {user.displayName?.charAt(0) || user.email?.charAt(0)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center group-hover:from-amber-500 group-hover:to-yellow-500 transition-all group-hover:shadow-lg ${
                    isActivePath('/profile') ? 'ring-2 ring-amber-500 shadow-md' : ''
                  }`}>
                    <span className="text-white text-sm font-bold">
                      {user.displayName?.charAt(0) || user.email?.charAt(0)}
                    </span>
                  </div>
                )}
                <span className={`text-[10px] font-medium transition-colors ${
                  isActivePath('/profile') ? 'text-amber-600' : 'text-gray-600 group-hover:text-amber-600'
                }`}>
                  Profile
                </span>
              </Link>
            </div>
          )}

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            {/* Main Navigation - Always visible */}
            <div className="space-y-1 px-3">
              {mainMenuItems.map((item) => 
                item.show ? (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg transition-all ${
                      isActivePath(item.path)
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    title={item.label}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-[9px] font-medium leading-tight">{item.label}</span>
                  </button>
                ) : null
              )}
            </div>

            {/* User-specific Navigation - Only when logged in */}
            {user && userMenuItems.some(item => item.show) && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-1 px-3">
                {userMenuItems.map((item) => 
                  item.show ? (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg transition-all ${
                        isActivePath(item.path)
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      title={item.label}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-[9px] font-medium leading-tight">{item.label}</span>
                    </button>
                  ) : null
                )}
              </div>
            )}
          </nav>

          {/* Logout Button */}
          {user && (
            <div className="p-3 border-t border-gray-200">
              <button
                onClick={handleSignOut}
                className="w-full flex flex-col items-center gap-1 px-3 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                title="Logout"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <span className="text-[9px] font-medium leading-tight">Logout</span>
              </button>
            </div>
          )}

          {/* Auth Button (when logged out) */}
          {!user && (
            <div className="p-3 border-t border-gray-200">
              <AuthButton />
            </div>
          )}
        </div>
      </aside>

      {/* Spacer for content (desktop only) - Always minimized width */}
      <div className="hidden lg:block w-20" />
    </>
  );
};

export default Sidebar;
